import React, { useRef, useEffect, useState } from 'react';
// @ts-ignore
import dropdownIcon from '../../image-assets/icon.arrows.dropdown.svg';
import editIcon from '../../image-assets/icon.edit.svg';
import deleteIcon from '../../image-assets/icon.delete.svg';
import copyIcon from '../../image-assets/icon.copy.svg';
import translateIcon from '../../image-assets/icon.tone.svg';
import { TranslationKey, TranslationString } from '../../services/translationService';
import { setContentWithHighlightedPlaceholders, extractPlaceholders } from '../../utils/placeholderUtils';
import './EditableTable.css';
import TextButton from '../Button/TextButton';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import { usePermissions } from '../../lib/permissions';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { useNotification } from '../../context/NotificationContext';
import IconTextButton from '../Button/IconTextButton';
import FixPlaceholderButton from '../FixPlaceholderButton/FixPlaceholderButton';
import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog';
import { animateTranslation } from '../../utils/translationAnimation';
import { aiTranslationService } from '../../services/translationAI';

interface TranslationData {
  key: TranslationKey;
  strings: TranslationString[];
}

interface Column {
  header: string;
  key: string;
  editable?: boolean;
  isSelectable?: boolean;
  isSelected?: boolean;
  onHeaderSelect?: () => void;
}

interface EditableTableProps {
  columns: Column[];
  data: TranslationData[];
  getRowKey: (row: TranslationData) => string;
  getCellContent: (row: TranslationData, columnKey: string) => string;
  onCellChange?: (rowKey: string, columnKey: string, value: string) => void;
  onDelete?: (rowKey: string) => void;
  selectedRows?: Set<string>;
  onRowSelect?: (rowKey: string, selected: boolean) => void;
  onCellFocus?: (rowKey: string, columnKey: string, content: string, sourceLanguage: string | null) => void;
  onCellBlur?: () => void;
  onTranslate?: (rowKey: string, columnKey: string, value: string) => void;
  targetLanguage?: string | null;
  sourceLanguage?: string | null;
  onTranslateCell?: (rowKey: string, columnKey: string, sourceText: string) => Promise<{ result: string } | null>;
  getCellActions?: (row: TranslationData, columnKey: string) => { label: string; onClick: () => void }[] | undefined;
  translatingCells?: Set<string>;
}

const EditableTable: React.FC<EditableTableProps> = ({
  columns,
  data,
  getRowKey,
  getCellContent,
  onCellChange,
  onDelete,
  selectedRows = new Set(),
  onRowSelect,
  onCellFocus,
  onCellBlur,
  onTranslate,
  targetLanguage,
  sourceLanguage,
  onTranslateCell,
  getCellActions,
  translatingCells = new Set(),
}) => {
  const { selectedTeam } = useWorkspace();
  const { user } = useAuth();
  const { profileData } = useProfile();
  const { permissions } = usePermissions(selectedTeam, user?.id, profileData?.id);
  const { showNotification } = useNotification();
  const cellRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
  const tableRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  
  // State for tracking focused cell
  const [focusedCell, setFocusedCell] = useState<{
    rowKey: string;
    columnKey: string;
    content: string;
    sourceLanguage: string | null;
  } | null>(null);

  // Store temporary value when focusing
  const tempValue = useRef<string>('');
  const [hasChanges, setHasChanges] = useState<{[key: string]: boolean}>({});

  // State for tracking the last focused cell
  const [lastFocusedCell, setLastFocusedCell] = useState<{
    rowKey: string;
    columnKey: string;
  } | null>(null);

  // Store original cell values when component mounts or data changes
  const originalDataValues = useRef<{[key: string]: {[columnKey: string]: string}}>({});
  
  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingBlurCell, setPendingBlurCell] = useState<{
    rowKey: string;
    columnKey: string;
    contentElement: HTMLElement;
    currentValue: string;
    originalContent: string;
  } | null>(null);
  
  // --- NEW STATE for managing the cell currently being edited --- 
  const [editingCell, setEditingCell] = useState<{
    rowKey: string;
    columnKey: string;
    value: string;
  } | null>(null);
  
  // Update originalDataValues when data changes
  useEffect(() => {
    const newValues: {[key: string]: {[columnKey: string]: string}} = {};
    data.forEach(row => {
      const rowKey = getRowKey(row);
      newValues[rowKey] = {};
      columns.forEach(column => {
        newValues[rowKey][column.key] = getCellContent(row, column.key);
      });
    });
    originalDataValues.current = newValues;
  }, [data, columns]);

  // Handle clicks on the document
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const grid = gridRef.current;
      
      // If we clicked outside the grid, check for unsaved changes
      if (grid && !grid.contains(target) && 
          !target.closest('.selection-action-bar') && 
          !target.closest('.translation-actions') && 
          !target.closest('.icon-text-button')) {
        
        // Find any active cell with unsaved changes
        const activeCell = document.activeElement?.closest('.editable-table-cell');
        if (activeCell) {
          const rowKey = activeCell.getAttribute('data-row');
          const columnKey = activeCell.getAttribute('data-col');
          
          if (rowKey && hasChanges[rowKey]) {
            const contentElement = activeCell.querySelector('.cell-content') as HTMLElement;
            if (contentElement) {
              const currentValue = contentElement.textContent || '';
              const row = data.find(r => getRowKey(r) === rowKey);
              if (row) {
                const originalContent = getCellContent(row, columnKey || '');
                
                if (currentValue !== originalContent) {
                  // Store the cell info for the confirmation dialog
                  setPendingBlurCell({
                    rowKey,
                    columnKey: columnKey || '',
                    contentElement,
                    currentValue,
                    originalContent
                  });
                  setShowConfirmDialog(true);
                  return;
                }
              }
            }
          }
        }
        
        // Blur any active cell
        const activeElement = document.activeElement;
        if (activeElement && grid.contains(activeElement)) {
          (activeElement as HTMLElement).blur();
          // Notify parent that cell is blurred
          onCellBlur?.();
        }
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onCellBlur, hasChanges, data]);

  // Handle confirmation dialog actions
  const handleConfirmSave = () => {
    if (pendingBlurCell) {
      const { rowKey, columnKey, currentValue } = pendingBlurCell;
      onCellChange?.(rowKey, columnKey, currentValue);
      tempValue.current = currentValue;
      setHasChanges(prev => ({ ...prev, [rowKey]: false }));
    }
    setShowConfirmDialog(false);
    setPendingBlurCell(null);
  };

  const handleConfirmCancel = () => {
    if (pendingBlurCell) {
      const { contentElement, originalContent } = pendingBlurCell;
      contentElement.textContent = originalContent;
      setContentWithHighlightedPlaceholders(contentElement, originalContent);
      setHasChanges(prev => ({ ...prev, [pendingBlurCell.rowKey]: false }));
    }
    setShowConfirmDialog(false);
    setPendingBlurCell(null);
  };

  // Handle cell focus
  const handleCellFocus = (rowKey: string, columnKey: string) => {
    // Update last focused cell
    setLastFocusedCell({ rowKey, columnKey });
    
    // --- Get initial content reliably from props --- 
    const row = data.find(r => getRowKey(r) === rowKey);
    const initialContent = row ? getCellContent(row, columnKey) : '';
    
    // Store the reliable initial content
    tempValue.current = initialContent;
    setHasChanges(prev => ({ ...prev, [rowKey]: false }));
    
    // --- Set the NEW editingCell state using reliable initial content --- 
    setEditingCell({ rowKey, columnKey, value: initialContent });
    
    // Call the parent's onCellFocus if provided 
    // (Pass the same reliable initial content)
     if (onCellFocus) {
       let sourceLanguage: string | null = null;
       if (columnKey !== 'key' && columns.length > 1) {
         for (let i = 0; i < columns.length; i++) {
           if (columns[i].key !== 'key' && columns[i].key !== columnKey) {
             sourceLanguage = columns[i].key;
             break;
           }
         }
       }
       // No need to find row/content again, we already have initialContent
       onCellFocus(rowKey, columnKey, initialContent, sourceLanguage);
     }
  };

  // Handle cell blur
  const handleCellBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const blurredElement = e.currentTarget;
    const currentEditingCell = editingCell; // Capture state at time of blur

    // Don't handle blur if we're in the process of translating or saving
    if (window.sessionStorage.getItem('texta_translating') || 
        window.sessionStorage.getItem('texta_saving')) {
      return;
    }

    let valueChanged = false;
    if (currentEditingCell && 
        currentEditingCell.rowKey === blurredElement.getAttribute('data-row') && 
        currentEditingCell.columnKey === blurredElement.getAttribute('data-col')) {
      
      // Save changes IF they exist (using hasChanges flag)
      if (hasChanges[currentEditingCell.rowKey]) {
        // --- Use editingCell.value for saving --- 
        let valueToSave = currentEditingCell.value;
        // --- Remove ALL trailing newlines --- 
        valueToSave = valueToSave.replace(/\n+$/, ''); 
        onCellChange?.(currentEditingCell.rowKey, currentEditingCell.columnKey, valueToSave);
        valueChanged = true;
      }
      
      // Reset states on blur
      setHasChanges(prev => ({
        ...prev,
        [currentEditingCell.rowKey]: false
      }));
      tempValue.current = '';
      setEditingCell(null); // Clear editing state
    }
    
    // Call the parent's onCellBlur callback if provided
    if (onCellBlur) {
      onCellBlur();
    }

    // Visual state restoration is handled by the ref callback now
  };

  // Handle cell content change via keyboard
  const handleCellChange = (e: React.FormEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const rowKey = target.getAttribute('data-row');
    const columnKey = target.getAttribute('data-col');
    
    if (rowKey && columnKey) {
      const currentValue = target.textContent || '';
      setHasChanges(prev => ({
        ...prev,
        [rowKey]: currentValue !== tempValue.current
      }));
    }
  };

  // Helper to get source text for a specific row
  const getSourceTextForRow = (rowKey: string): string => {
    if (!sourceLanguage) return '';
    
    const row = data.find(row => getRowKey(row) === rowKey);
    if (!row) return '';
    
    return getCellContent(row, sourceLanguage);
  };

  // Handle cell translation
  const handleTranslateCell = async (rowKey: string, columnKey: string, sourceText: string) => {
    try {
      const translatedText = await aiTranslationService.translateText(sourceText, columnKey);
      
      if (!translatedText) return null;

      // Find the cell element
      const cellElement = document.querySelector(`.editable-table-cell[data-row="${rowKey}"][data-col="${columnKey}"]`);
      if (!cellElement) {
        return null;
      }

      // Animate the translation
      await animateTranslation({
        cellElement: cellElement as HTMLElement,
        translatedText,
        onComplete: () => {
          // Update the data model
          onCellChange?.(rowKey, columnKey, translatedText);
        }
      });

      return { result: translatedText };
    } catch (error) {
      console.error('Error translating cell:', error);
      showNotification('Failed to translate');
      return null;
    }
  };

  // Function to check if a cell is missing placeholders
  const hasMissingPlaceholders = (rowKey: string, columnKey: string): boolean => {
    // Skip the key column
    if (columnKey === 'key') return false;
    
    // Get the first non-key column (second column)
    const firstLangColumn = columns.find(col => col.key !== 'key');
    if (!firstLangColumn) return false;
    
    // Only show error on the third column (target language)
    if (columnKey === firstLangColumn.key) return false;
    
    // Find the row data
    const row = data.find(row => getRowKey(row) === rowKey);
    if (!row) return false;
    
    // Get first language text from the row's strings (second column)
    const firstLangString = row.strings.find(s => s.language_code === firstLangColumn.key);
    if (!firstLangString) return false;
    
    // Get current text from the row's strings (third column)
    const currentString = row.strings.find(s => s.language_code === columnKey);
    if (!currentString || !currentString.value.trim()) return false;
    
    // Extract placeholders from both texts
    const firstLangPlaceholders = extractPlaceholders(firstLangString.value);
    const currentPlaceholders = extractPlaceholders(currentString.value);
    
    // Check if any placeholders from first language are missing in current text
    // Also check if any placeholders in current text are not in first language
    return firstLangPlaceholders.some(placeholder => !currentPlaceholders.includes(placeholder)) ||
           currentPlaceholders.some(placeholder => !firstLangPlaceholders.includes(placeholder));
  };

  // Handle save button click
  const handleSaveClick = (e: React.MouseEvent<HTMLButtonElement>, rowKey: string, columnKey: string) => {
    e.stopPropagation(); // Prevent blur event
    const cell = e.currentTarget.closest('.editable-table-cell');
    // --- Use editingCell.value for saving --- 
    if (cell && editingCell && editingCell.rowKey === rowKey && editingCell.columnKey === columnKey) {
      let valueToSave = editingCell.value;
      // --- Remove ALL trailing newlines --- 
      valueToSave = valueToSave.replace(/\n+$/, ''); 
      console.log(`EditableTable (SaveBtn): Saving value from editingCell state for ${rowKey}-${columnKey}:`, JSON.stringify(valueToSave));
        
      // Set saving flag
      window.sessionStorage.setItem('texta_saving', 'true');
      
      // Update hasChanges state first
      setHasChanges(prev => ({ ...prev, [rowKey]: false }));
      
      // Save changes
      onCellChange?.(rowKey, columnKey, valueToSave);
      
      // Update tempValue (maybe not needed anymore?)
      tempValue.current = valueToSave;

      // Clear editing state AFTER saving
      setEditingCell(null);
      
      // Blur the input after saving
      const contentElement = cell.querySelector('.cell-content') as HTMLElement;
      contentElement?.blur();
      
      // Clear saving flag after delay
      setTimeout(() => {
        window.sessionStorage.removeItem('texta_saving');
      }, 100);
    } else {
       console.warn('Save click happened but no matching editingCell state found');
    }
  };

  return (
    <div className="editable-table" ref={tableRef}>
      <div className="editable-table-headers">
        {columns.map((column) => (
          <div key={column.key} className="editable-table-header">
            {column.isSelectable && (
              <input
                type="checkbox"
                className="header-checkbox"
                checked={column.isSelected}
                onChange={column.onHeaderSelect}
              />
            )}
            <img src={dropdownIcon} alt="Dropdown" className="header-icon" />
            <span>{column.header}</span>
          </div>
        ))}
      </div>

      <div 
        className="editable-table-grid" 
        ref={gridRef}
        onMouseDown={(e) => {
          // Only handle clicks within the grid
          const target = e.target as HTMLElement;
          if (!target.classList.contains('editable-table-cell')) {
            // If we clicked the grid but not a cell, blur any active cell
            const activeElement = document.activeElement;
            if (activeElement && activeElement.classList.contains('editable-table-cell')) {
              (activeElement as HTMLElement).blur();
            }
          }
        }}
      >
        {data.map((row, rowIndex) => (
          <React.Fragment key={getRowKey(row)}>
            {columns.map((column, columnIndex) => {
              const rowKey = getRowKey(row);
              // Get the translation string ID if it exists
              const translationString = columnIndex !== 0 ? row.strings?.find(s => s.language_code === column.key) : null;
              // Use rowKey directly without appending columnKey
              const cellKey = rowKey;
              
              const content = getCellContent(row, column.key);
              const isCellEditable = column.editable !== false;
              const isTargetLang = column.key === targetLanguage;
              const hasSourceText = isTargetLang && getSourceTextForRow(rowKey).trim() !== '';
              const isMissingPlaceholders = hasMissingPlaceholders(rowKey, column.key);
              const isTranslating = translatingCells.has(cellKey);
              
              return (
                <div
                  key={`${cellKey}-${column.key}`}
                  id={cellKey}
                  data-cell-key={cellKey}
                  data-row={rowKey}
                  data-col={column.key}
                  className={`editable-table-cell ${columnIndex === 0 && selectedRows.has(rowKey) ? 'selected' : ''} ${translatingCells.has(`${rowKey}-${column.key}`) ? 'translating' : ''}`}
                  ref={el => {
                    if (el) {
                      cellRefs.current[`${rowKey}-${column.key}`] = el;
                    }
                  }}
                  onClick={(e) => {
                    if (columnIndex === 0) {
                      const target = e.target as HTMLElement;
                      if (target.classList.contains('row-checkbox')) {
                        const isChecked = (target as HTMLInputElement).checked;
                        onRowSelect?.(rowKey, isChecked);
                      }
                    }
                  }}
                >
                  {columnIndex === 0 && (
                    <input
                      type="checkbox"
                      className="row-checkbox"
                      checked={selectedRows.has(rowKey)}
                      onChange={(e) => onRowSelect?.(rowKey, e.target.checked)}
                    />
                  )}
                  
                  {/* Container for the editable content */}
                  <div className="cell-inner-container">
                    {/* Actual editable content */}
                    <div 
                      className={`cell-content ${isMissingPlaceholders ? 'missing-placeholder' : ''}`}
                      data-missing-placeholders={isMissingPlaceholders}
                      contentEditable={columnIndex !== 0 && isCellEditable}
                      suppressContentEditableWarning
                      // Add title attribute ONLY for the key column (index 0)
                      title={columnIndex === 0 ? content : undefined}
                      onFocus={(e) => {
                        if (columnIndex !== 0 && isCellEditable) {
                          // Call internal handler to set state etc.
                          handleCellFocus(
                            rowKey,
                            column.key
                          );
                          // REMOVED: Explicit reset of textContent
                        }
                      }}
                      onBlur={(e) => {
                        if (columnIndex !== 0) {
                          handleCellBlur(e);
                        }
                      }}
                      onInput={(e) => {
                        if (columnIndex !== 0) {
                          const target = e.target as HTMLElement;
                          const currentValue = target.textContent || '';
                          
                          // --- REMOVED LOGGING --- 
                          // console.log(`EditableTable (onInput): textContent read:`, JSON.stringify(currentValue));

                          // Update the editingCell state 
                          setEditingCell(prev => 
                            prev && prev.rowKey === rowKey && prev.columnKey === column.key 
                            ? { ...prev, value: currentValue }
                            : prev 
                          );
                          
                          // Update hasChanges state
                          setHasChanges(prev => ({
                            ...prev,
                            [rowKey]: currentValue !== tempValue.current
                          }));
                        }
                      }}
                      onKeyDown={(e) => {
                        if (columnIndex === 0) {
                          // Prevent any keyboard events on the first column
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }

                        // Handle Enter key
                        if (e.key === 'Enter') {
                          if (!e.shiftKey) {
                            e.preventDefault();
                            e.stopPropagation();

                            // --- Use editingCell.value for saving --- 
                            if (editingCell && editingCell.rowKey === rowKey && editingCell.columnKey === column.key) {
                              let valueToSave = editingCell.value;
                              // --- Remove ALL trailing newlines --- 
                              valueToSave = valueToSave.replace(/\n+$/, ''); 
                              // REMOVED console.log
                           
                              // Set a flag to prevent blur from reverting changes
                              window.sessionStorage.setItem('texta_saving', 'true');
                              
                              // Update hasChanges state first to prevent reversion
                              setHasChanges(prev => ({ ...prev, [rowKey]: false }));
                              
                              // Save changes
                              onCellChange?.(rowKey, column.key, valueToSave);
                              
                              // Update temp value (maybe not needed?)
                              tempValue.current = valueToSave;
                              
                              // Clear editing state AFTER saving
                              setEditingCell(null);

                              // Force blur to exit edit mode
                              e.currentTarget.blur();
                              
                              // Clear the flag after a short delay
                              setTimeout(() => {
                                window.sessionStorage.removeItem('texta_saving');
                              }, 100);
                            } else {
                               // REMOVED console.warn
                            }
                          } 
                        }
                      }}
                      ref={(el) => {
                        if (el) {
                          const isCurrentlyEditingThisCell = 
                            editingCell?.rowKey === rowKey && 
                            editingCell?.columnKey === column.key;

                          // Only modify display if the element is NOT the one being edited
                          if (!isCurrentlyEditingThisCell) {
                            // Apply visual state *only* for non-key columns when not focused
                            if (columnIndex === 0) {
                              // Key column (not editing): just set raw text content
                              // REMOVED console.log
                              el.textContent = content; // Use content prop here
                            } else {
                              // Translation column (not editing): apply visual state (hidden chars, placeholders)
                              // REMOVED console.log
                              setContentWithHighlightedPlaceholders(el, content); // Use content prop here
                            }
                          } else {
                            // Element is being edited: Do nothing here. 
                            // Let contentEditable and onInput handler manage the display.
                            // REMOVED console.log
                          }
                        }
                      }}
                    >
                      {/* Content will be set by ref callback */}
                    </div>
                  </div>
                  
                  {columnIndex === 0 && (
                    <div className="icon-container">
                      <img 
                        src={copyIcon} 
                        alt="Copy" 
                        onClick={(e) => {
                          e.stopPropagation();
                          const content = getCellContent(row, column.key);
                          navigator.clipboard.writeText(content).then(() => {
                            // Optional: Add visual feedback that copy succeeded
                            const img = e.target as HTMLImageElement;
                            const originalOpacity = img.style.opacity;
                            img.style.opacity = '1';
                            setTimeout(() => {
                              img.style.opacity = originalOpacity;
                            }, 200);
                          });
                        }}
                      />
                      {onDelete && permissions.canManageKeys && (
                        <img 
                          src={editIcon} 
                          alt="Edit" 
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(rowKey);
                          }}
                        />
                      )}
                    </div>
                  )}
                  
                  {/* Show translate button container with save and fix placeholder buttons */}
                  {(isTargetLang && hasSourceText) || isMissingPlaceholders || hasChanges[rowKey] ? (
                    <div className="cell-translate-button-container">
                      {isTargetLang && hasSourceText && (
                        <TextButton
                          variant="text"
                          size="small"
                          className="cell-translate-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            
                            requestAnimationFrame(() => {
                              handleTranslateCell(rowKey, column.key, getSourceTextForRow(rowKey));
                            });
                          }}
                          onMouseDown={e => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                        >
                          <img src={translateIcon} alt="Translate" />
                          Translate
                        </TextButton>
                      )}
                      {isMissingPlaceholders && (
                        <FixPlaceholderButton
                          onClick={() => handleTranslateCell(rowKey, column.key, getSourceTextForRow(rowKey))}
                        />
                      )}
                      {hasChanges[rowKey] && (
                        <TextButton
                          variant="primary"
                          size="small"
                          className="cell-save-button"
                          onClick={(e) => handleSaveClick(e, rowKey, column.key)}
                        >
                          Save
                        </TextButton>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={handleConfirmCancel}
        onConfirm={handleConfirmSave}
        message="You have unsaved changes. Would you like to save them?"
        confirmText="Save"
        cancelText="Discard"
      />
    </div>
  );
};

export default EditableTable;